import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { reportsService } from '../../services/reports';

export const fetchDashboardData = createAsyncThunk(
  'reports/fetchDashboardData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await reportsService.getDashboardData();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dashboard data');
    }
  }
);

export const fetchWebhookStats = createAsyncThunk(
  'reports/fetchWebhookStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await reportsService.getWebhookStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch webhook stats');
    }
  }
);

export const fetchReportsData = createAsyncThunk(
  'reports/fetchReportsData',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await reportsService.getReportsData(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch reports data');
    }
  }
);

export const fetchAnalyticsData = createAsyncThunk(
  'reports/fetchAnalyticsData',
  async (params, { rejectWithValue }) => {
    try {
      const response = await reportsService.getAnalyticsData(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics data');
    }
  }
);

export const fetchTeamPerformance = createAsyncThunk(
  'reports/fetchTeamPerformance',
  async (params, { rejectWithValue }) => {
    try {
      const response = await reportsService.getTeamPerformance(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch team performance');
    }
  }
);

export const generateReport = createAsyncThunk(
  'reports/generateReport',
  async (reportData, { rejectWithValue }) => {
    try {
      const response = await reportsService.generateReport(reportData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to generate report');
    }
  }
);

const reportsSlice = createSlice({
  name: 'reports',
  initialState: {
    dashboardData: null,
    reportsData: null,
    analyticsData: null,
    webhookStats: null,
    teamPerformance: null,
    reports: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardData = action.payload;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchWebhookStats.fulfilled, (state, action) => {
        state.webhookStats = action.payload;
      })
      .addCase(fetchReportsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportsData.fulfilled, (state, action) => {
        state.loading = false;
        state.reportsData = action.payload;
      })
      .addCase(fetchReportsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAnalyticsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnalyticsData.fulfilled, (state, action) => {
        state.loading = false;
        state.analyticsData = action.payload;
      })
      .addCase(fetchAnalyticsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTeamPerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeamPerformance.fulfilled, (state, action) => {
        state.loading = false;
        state.teamPerformance = action.payload;
      })
      .addCase(fetchTeamPerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(generateReport.fulfilled, (state, action) => {
        state.reports.unshift(action.payload);
      });
  },
});

export const { clearError } = reportsSlice.actions;
export default reportsSlice.reducer;
